import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';

import useLoginForward from '@ecp/common/src/hooks/useLoginForward';
import moTheme from '@ecp/common/src/style/theme/mo';
import { FlexBox, FlexBoxColumn, ImageBox, FlexBoxCenter } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import StarRatingIcon from '@ecp/common/src/components/icon/StarRatingIcon';
import { BADGE_TYPE, PD_EVALUATION_DIVI } from '@ecp/common/src/const/good';
import { SITE_SKYPASS } from '@ecp/common/src/const/site/siteNumberConst.js';
import adultPath from '@ecp/common/src/assets/icon/icon__adult-certification.svg';

import { withAdultCertification, getIsLogin } from '@mo-recoil/common/user/selector';
import { useAlertDialog, useConfirmDialog } from '@mo-hooks/common/useModal';
import { useDisplayPrice } from '@mo-hooks/price/DisplayPriceHook';
import useSiteInfo from '@mo-hooks/common/useSiteInfo';
import goodApi from '@mo-apis/good/goodApi';
import recopickApi from '@mo-apis/interface/recopickApi';
import GoodBenefitChip from '@mo-components/good/GoodBenefitChip';
import GoodBadge from '@mo-components/good/GoodBadge';
import CartPopup from '@mo-pages/good/components/popup/CartPopup';
import { cardProps, goodCardPropTypes } from '@mo-const/good/goodCardConst';
import { ReactComponent as DoubleQuotation } from '@mo-assets/icon/icon__review__double-quotation.svg';

const MAX_COUNT = 999;

const GoodOriginalPrice = ({ type, originalPrice, originalFormattedPrice, discountPrice }) => {
  const theme = useTheme();
  const { priceFormat } = useSiteInfo();

  const internalOriginalPrice = useDisplayPrice(
    Number(originalPrice).toLocaleString(),
    Number(originalFormattedPrice).toLocaleString()
  );

  const showOriginalPrice = useMemo(
    () => !!originalPrice && originalPrice > discountPrice,
    [discountPrice, originalPrice]
  );

  return (
    <>
      {showOriginalPrice && (
        <TextBox
          decoration={'line-through'}
          hoverDecoration={'line-through'}
          size={cardProps[type].size.originalPrice}
          color={theme.color.text.disabled}
          height={moTheme.font.lineHeight.bold}
        >
          <BlindSpan>정가</BlindSpan>
          {`${internalOriginalPrice}${priceFormat}`}
        </TextBox>
      )}
    </>
  );
};

GoodOriginalPrice.propTypes = {
  type: PropTypes.string,
  originalPrice: PropTypes.number,
  originalFormattedPrice: PropTypes.number,
  discountPrice: PropTypes.number,
};

const BlindSpan = styled.span`
  over-flow: hidden;
  position: absolute;
  clip: rect(0, 0, 0, 0);
  clip-path: polygon(0 0, 0 0, 00);
  width: 1px;
  height: 1px;
  margin: -1px;
`;

export const GoodPriceBox = ({
  type,
  originalPrice,
  originalFormattedPrice,
  discountRate,
  discountPrice,
  discountFormattedPrice,
  'align-items': alignItems = 'flex-start',
}) => {
  const theme = useTheme();
  const { priceFormat } = useSiteInfo();
  const isOriginalPricePositionTop = useMemo(() => 'horizontalSmall' !== type, [type]);

  const internalDiscountPrice = useDisplayPrice(
    Number(discountPrice).toLocaleString(),
    Number(discountFormattedPrice).toLocaleString()
  );

  return (
    <FlexBoxColumn align-items={alignItems} width={'100%'}>
      {isOriginalPricePositionTop && (
        <GoodOriginalPrice
          type={type}
          originalPrice={originalPrice}
          originalFormattedPrice={originalFormattedPrice}
          discountPrice={discountPrice}
        />
      )}
      <FlexBox align-items={'baseline'} flex-wrap={'wrap'}>
        {!!discountRate && (
          <FlexBox>
            <TextBox
              height={'fit-content'}
              align-items={'baseline'}
              size={cardProps[type].size.discountRate}
              weight={moTheme.font.weight.bold}
              color={theme.color.primary}
            >
              <BlindSpan>할인율</BlindSpan>
              {discountRate}
              <TextBox
                size={cardProps[type].size.percent}
                weight={moTheme.font.weight.bold}
                color={theme.color.primary}
              >
                %
              </TextBox>
            </TextBox>
            <Spacing left={'6px'} />
          </FlexBox>
        )}
        <TextBox
          align-items={'baseline'}
          size={cardProps[type].size.discountPrice}
          weight={moTheme.font.weight.bold}
          height={moTheme.font.lineHeight.bold}
        >
          <BlindSpan>판매가</BlindSpan>
          {internalDiscountPrice}
          <TextBox size={cardProps[type].size.won} height={moTheme.font.lineHeight.bold}>
            {priceFormat}
          </TextBox>
        </TextBox>
        <Spacing left={'6px'} />
        {!isOriginalPricePositionTop && (
          <GoodOriginalPrice
            type={type}
            originalPrice={originalPrice}
            originalFormattedPrice={originalFormattedPrice}
            discountPrice={discountPrice}
          />
        )}
      </FlexBox>
    </FlexBoxColumn>
  );
};

const GoodPriceBoxPropTypes = {
  originalPrice: PropTypes.number,
  originalFormattedPrice: PropTypes.number,
  discountRate: PropTypes.number,
  discountPrice: PropTypes.number,
  discountFormattedPrice: PropTypes.number,
  type: PropTypes.oneOf(['extraSmall', 'small', 'medium', 'large', 'horizontalSmall', 'horizontalMedium', 'review']),
  'align-items': PropTypes.string,
};
GoodPriceBox.propTypes = GoodPriceBoxPropTypes;

export const GoodImage = ({ adultGood, soldOut, type, imgPath }) => {
  const theme = useTheme();
  const isAdultVerified = useRecoilValue(withAdultCertification) || !adultGood;
  // const showAdultMessage = useMemo(() => !isAdultVerified && !!cardProps[type].size.adult, [isAdultVerified, type]);

  return (
    <FlexBox>
      <ImageBox
        width={cardProps[type].imageWidth}
        height={cardProps[type].imageWidth}
        imageSrc={imgPath}
        radius={'6px'}
        border={`1px solid ${theme.color.line.basic}`}
        backgroundPositionY={'center'}
        justify-content={'center'}
        align-items={'center'}
      >
        {!isAdultVerified && (
          <ImageBox
            clickable
            width={cardProps[type].imageWidth}
            height={cardProps[type].imageWidth}
            imageSrc={adultPath}
            backgroundPositionY={'center'}
            backgroundPositionX={'center'}
            justify-content={'center'}
            align-items={'center'}
          ></ImageBox>
        )}
        {/* <TextBox size={cardProps[type].size.adult} padding={'62px 0 0 0'} height={'16px'}>
          {!isAdultVerified ? '19세 이상만 구매 가능' : ''}
        </TextBox> */}
      </ImageBox>
      {soldOut && (
        <SoldOut height={cardProps[type]?.soldOutHeight}>
          <TextBox color={theme.color.text.white} size={moTheme.font.size.$12} weight={moTheme.font.weight.medium}>
            품절
          </TextBox>
        </SoldOut>
      )}
    </FlexBox>
  );
};

const SoldOut = styled(FlexBoxCenter)`
  position: absolute;
  background-color: rgba(20, 20, 21, 0.5);
  width: 100%;
  height: ${({ height }) => height};
  bottom: 0;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

GoodImage.propTypes = {
  adultGood: PropTypes.bool,
  soldOut: PropTypes.bool,
  type: PropTypes.string,
  imgPath: PropTypes.string,
};

export const GoodCardImage = ({
  onClick,
  handleHotTopAllCategorySoldOutChangeList,
  handleViewGoodSoldOutChangeList,
  handleOrderGoodSoldOutChangeList,
  selectedCategoryNumber,
  handelBestViewGoodSoldOutChangeList,
  handleBestOrderGoodSoldOutChangeList,
  handleSubDisplayCategoryGoodSoldOutChangeList,
  handleCartPopupOpen,
  ...props
}) => {
  const {
    thumbnailImageUrl02,
    thumbnailImageUrl03,
    itemDecorators,
    type,
    adultGood,
    wish,
    soldOut,
    showRelated,
    clickLogLink,
    goodName,
    goodCardCartBtnUseYn,
    showBtnCart,
  } = props;

  const login = useRecoilValue(getIsLogin);
  const { siteNumber } = useSiteInfo();
  const { showConfirmDialog } = useConfirmDialog();
  const loginForward = useLoginForward(siteNumber, showConfirmDialog);

  const imgPath = useMemo(() => {
    switch (type) {
      case 'large':
        return thumbnailImageUrl02;
      case 'extraSmall':
      case 'small':
      case 'medium':
      case 'horizontalSmall':
      case 'horizontalMedium':
      case 'review':
      default:
        return thumbnailImageUrl03;
    }
  }, [thumbnailImageUrl02, thumbnailImageUrl03, type]);

  const decorators = useMemo(() => {
    const itemDecoratorList = Array.isArray(itemDecorators) ? itemDecorators : [];
    return [
      ...itemDecoratorList,
      { type: BADGE_TYPE.RELATED, badgeContent: showRelated },
      //위시 삭제, 장바구니로 교체
      goodCardCartBtnUseYn === 'Y' && showBtnCart && !soldOut
        ? { type: BADGE_TYPE.CART, badgeContent: '' }
        : { type: null, badgeContent: null },
    ];
  }, [itemDecorators, showRelated, wish, goodCardCartBtnUseYn, showBtnCart, soldOut]);

  const getChangeHandler = (type, onChange) => {
    switch (type) {
      case BADGE_TYPE.CART:
        return () => {
          if (!login) {
            loginForward();
            return;
          }
          handleCartPopupOpen(true);
        };
      default:
        return onChange;
    }
  };

  const { handleClick } = useGoodCardClick({
    onClick,
    clickLogLink,
    props,
    handleHotTopAllCategorySoldOutChangeList,
    handleViewGoodSoldOutChangeList,
    handleOrderGoodSoldOutChangeList,
    selectedCategoryNumber,
    handelBestViewGoodSoldOutChangeList,
    handleBestOrderGoodSoldOutChangeList,
    handleSubDisplayCategoryGoodSoldOutChangeList,
  });

  return (
    <FlexBox width={cardProps[type].imageWidth} height={cardProps[type].imageWidth} onClick={handleClick}>
      {decorators.reduce(
        (accumulator, item) => (
          <GoodBadge
            imageWidth={cardProps[type].imageWidth}
            {...item}
            onChange={getChangeHandler(item?.type, item?.onChange)}
            title={goodName}
          >
            {accumulator}
          </GoodBadge>
        ),
        <GoodImage adultGood={adultGood} soldOut={soldOut} type={type} imgPath={imgPath} />
      )}
    </FlexBox>
  );
};

GoodCardImage.propTypes = {
  ...goodCardPropTypes,
  handleCartPopupOpen: PropTypes.func,
};

const useGoodCardClick = ({
  onClick,
  clickLogLink,
  props,
  handleHotTopAllCategorySoldOutChangeList,
  handleViewGoodSoldOutChangeList,
  handleOrderGoodSoldOutChangeList,
  handelBestViewGoodSoldOutChangeList,
  handleBestOrderGoodSoldOutChangeList,
  handleSubDisplayCategoryGoodSoldOutChangeList,
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { showAlertDialog } = useAlertDialog();
  const { goodNumber, fromExhibition } = props;
  const login = useRecoilValue(getIsLogin);
  const { siteNumber } = useSiteInfo();
  const isSkypass = useMemo(() => siteNumber === SITE_SKYPASS, [siteNumber]);

  const handleClick = async (e) => {
    e.stopPropagation();

    if (onClick) {
      onClick(e, goodNumber);
    } else {
      if (clickLogLink) {
        const url = new URL(clickLogLink);
        const queryParams = new URLSearchParams(url?.search);
        const { code } = await goodApi.getStockCheck(goodNumber);

        if (code) {
          showAlertDialog('죄송합니다.\n해당 상품은 품절되었습니다.', () => {
            if (handleHotTopAllCategorySoldOutChangeList !== undefined) {
              // main 구매Top, 조회Top
              handleHotTopAllCategorySoldOutChangeList();
            } else if (handleViewGoodSoldOutChangeList !== undefined) {
              // main 조회Top 하단 카테고리
              handleViewGoodSoldOutChangeList(state?.categoryNumber);
            } else if (handleOrderGoodSoldOutChangeList !== undefined) {
              // main 구매Top 하단 카테고리
              handleOrderGoodSoldOutChangeList(state?.categoryNumber);
            } else if (handelBestViewGoodSoldOutChangeList !== undefined) {
              // best 조회Top
              handelBestViewGoodSoldOutChangeList(state?.categoryNumber);
            } else if (handleBestOrderGoodSoldOutChangeList !== undefined) {
              // best 구매Top
              handleBestOrderGoodSoldOutChangeList(state?.categoryNumber);
            } else if (handleSubDisplayCategoryGoodSoldOutChangeList !== undefined) {
              // 서브 카테고리
              handleSubDisplayCategoryGoodSoldOutChangeList();
            }
          });
        } else {
          navigate(`/good/${goodNumber}?recopick=${queryParams.get('method')}&product_type=R`, {
            state: { ...state, fromExhibition: fromExhibition },
          });
        }
      } else {
        const { code } = await goodApi.getStockCheck(goodNumber);
        if (code) {
          showAlertDialog('죄송합니다.\n해당 상품은 품절되었습니다.', () => {
            if (handleHotTopAllCategorySoldOutChangeList !== undefined) {
              // main 구매Top, 조회Top
              handleHotTopAllCategorySoldOutChangeList();
            } else if (handleViewGoodSoldOutChangeList !== undefined) {
              // main 조회Top 하단 카테고리
              handleViewGoodSoldOutChangeList(state?.categoryNumber);
            } else if (handleOrderGoodSoldOutChangeList !== undefined) {
              // main 구매Top 하단 카테고리
              handleOrderGoodSoldOutChangeList(state?.categoryNumber);
            } else if (handelBestViewGoodSoldOutChangeList !== undefined) {
              // best 조회Top
              handelBestViewGoodSoldOutChangeList(state?.categoryNumber);
            } else if (handleBestOrderGoodSoldOutChangeList !== undefined) {
              // best 구매Top
              handleBestOrderGoodSoldOutChangeList(state?.categoryNumber);
            } else if (handleSubDisplayCategoryGoodSoldOutChangeList !== undefined) {
              // 서브 카테고리
              handleSubDisplayCategoryGoodSoldOutChangeList();
            }
          });
        } else {
          navigate(`/good/${goodNumber}`, { state: { ...state, fromExhibition: fromExhibition } });
        }
      }
    }
    if (clickLogLink && login && !isSkypass) {
      await recopickApi.createRecommendationClickLog(clickLogLink);
    }
  };
  return { handleClick };
};

const GoodCard = ({
  onClick,
  handleHotTopAllCategorySoldOutChangeList,
  handleViewGoodSoldOutChangeList,
  handleOrderGoodSoldOutChangeList,
  selectedCategoryNumber,
  handelBestViewGoodSoldOutChangeList,
  handleBestOrderGoodSoldOutChangeList,
  handleSubDisplayCategoryGoodSoldOutChangeList,
  ...props
}) => {
  const theme = useTheme();
  const {
    goodName,
    originalPrice,
    originalFormattedPrice,
    discountRate,
    discountPrice,
    discountFormattedPrice,
    totalReviewScore,
    reviewCount,
    buyCount = 0,
    flagList,
    hideBestFlag,
    type,
    review,
    onReviewClick,
    buyCountDisplay,
    evaluationDivisionCode,
    clickLogLink,
    goodNumber,
    isEventMall,
    handleCartPopupSuccess,
  } = props;

  const priceBoxProps = {
    type,
    originalPrice,
    originalFormattedPrice,
    discountRate,
    discountPrice,
    discountFormattedPrice,
  };
  const [cartPopupOpen, setCartPopupOpen] = useState(false);
  const {
    isPriceOpen,
    systemInformation: { goodCardCartBtnUseYn },
    siteNumber,
  } = useSiteInfo();
  const imageSpacing = useMemo(
    () => ({ left: type.includes('horizontal') ? '14px' : '10px', top: type.includes('horizontal') ? '0px' : '10px' }),
    [type]
  );

  const showRating = useMemo(() => {
    const isAvailableCode = evaluationDivisionCode !== PD_EVALUATION_DIVI.UNAVAILABLE;
    const isSkypass = siteNumber === SITE_SKYPASS;

    return isAvailableCode && !isSkypass;
  }, [evaluationDivisionCode, siteNumber]);

  const filteredFlagList = useMemo(() => {
    const defaultFilteredFlagList = flagList?.filter((it) => !(hideBestFlag && it === 'BEST'));
    const isSkypass = siteNumber === SITE_SKYPASS;

    if (isSkypass) {
      // 대한항공의 경우 모든 플래그 제거
      return [];
    } else {
      return defaultFilteredFlagList;
    }
  }, [flagList, hideBestFlag, siteNumber]);

  const { handleClick } = useGoodCardClick({
    onClick,
    clickLogLink,
    props,
    handleHotTopAllCategorySoldOutChangeList,
    handleViewGoodSoldOutChangeList,
    handleOrderGoodSoldOutChangeList,
    selectedCategoryNumber,
    handelBestViewGoodSoldOutChangeList,
    handleBestOrderGoodSoldOutChangeList,
    handleSubDisplayCategoryGoodSoldOutChangeList,
  });

  const handleReviewClick = (e) => {
    e.stopPropagation();
    onReviewClick?.(review);
  };

  const displayReviewCount = useMemo(() => {
    return reviewCount > MAX_COUNT ? `${MAX_COUNT}+` : reviewCount;
  }, [reviewCount]);

  const displayBuyCount = useMemo(() => {
    return buyCount > MAX_COUNT ? `${MAX_COUNT}+` : buyCount;
  }, [buyCount]);

  return (
    <>
      <GoodCardRoot type={type} onClick={handleClick} data-ds-contents={goodNumber}>
        <GoodCardImage
          handleHotTopAllCategorySoldOutChangeList={handleHotTopAllCategorySoldOutChangeList}
          handleViewGoodSoldOutChangeList={handleViewGoodSoldOutChangeList}
          handleOrderGoodSoldOutChangeList={handleOrderGoodSoldOutChangeList}
          selectedCategoryNumber={selectedCategoryNumber}
          handelBestViewGoodSoldOutChangeList={handelBestViewGoodSoldOutChangeList}
          handleBestOrderGoodSoldOutChangeList={handleBestOrderGoodSoldOutChangeList}
          handleSubDisplayCategoryGoodSoldOutChangeList={handleSubDisplayCategoryGoodSoldOutChangeList}
          goodCardCartBtnUseYn={goodCardCartBtnUseYn}
          handleCartPopupOpen={setCartPopupOpen}
          {...props}
        />
        <Spacing left={imageSpacing.left} top={imageSpacing.top} />
        {'review' === type && (
          <FlexBox width={'20px'}>
            <DoubleQuotation width={'15px'} height={'12px'} />
            <Spacing left={'5px'} />
          </FlexBox>
        )}
        <FlexBoxColumn width={'100%'} height={'100%'} justify-content={'space-between'}>
          {!isEventMall && filteredFlagList?.length > 0 && (
            <>
              <FlexBox width={'100%'} flex-wrap={'wrap'}>
                {filteredFlagList.map((item, index) => (
                  <Spacing right={'4px'} bottom={'4px'} key={index}>
                    <GoodBenefitChip
                      item={item}
                      size={cardProps[type].size.flag}
                      height={cardProps[type].lineHeight.flag}
                    />
                  </Spacing>
                ))}
              </FlexBox>
            </>
          )}
          {'review' === type && (
            <>
              <ReviewContentWrapper onClick={handleReviewClick}>
                <ReviewContent>{review.content}</ReviewContent>
              </ReviewContentWrapper>
              <Spacing top={'14px'} />
            </>
          )}
          <StyledTitle size={cardProps[type].size.goodName} lineHeight={cardProps[type].lineHeight.goodName}>
            {goodName}
          </StyledTitle>
          <Spacing top={cardProps[type].titleGap} width={'100%'}>
            {(isPriceOpen === undefined ? true : isPriceOpen) && <GoodPriceBox {...priceBoxProps} />}
            {totalReviewScore !== undefined && (showRating || buyCountDisplay) && (
              <Spacing top={'6px'} width={'100%'}>
                <FlexBox
                  width={'100%'}
                  align-items={'center'}
                  flex-wrap={'wrap'}
                  minHeight={cardProps[type].lineHeight.totalReviewScore}
                >
                  {showRating && (
                    <>
                      <StarRatingIcon size={cardProps[type].size.totalReviewScore} fillPercent={100} />
                      <Spacing left={'3px'} />
                      <TextBox size={cardProps[type].size.totalReviewScore} color={theme.color.text.guide}>
                        <BlindP>총 별점 5점 만점에</BlindP>
                        <p>{totalReviewScore?.toFixed?.(1)}</p>
                        <BlindP>리뷰 수</BlindP>
                        <p>({displayReviewCount})</p>
                      </TextBox>
                      <Spacing left={'6px'} />
                    </>
                  )}
                  {buyCountDisplay && (
                    <TextBox
                      size={cardProps[type].size.totalReviewScore}
                      color={theme.color.text.guide}
                      align-items={'baseline'}
                    >
                      {`구매 `}
                      <TextBox size={cardProps[type].size.totalReviewScore} color={theme.color.primary}>
                        {displayBuyCount}
                      </TextBox>
                    </TextBox>
                  )}
                </FlexBox>
              </Spacing>
            )}
          </Spacing>
        </FlexBoxColumn>
      </GoodCardRoot>
      <CartPopup
        goodNumber={goodNumber}
        open={cartPopupOpen}
        setCartPopupOpen={setCartPopupOpen}
        isPriceOpen={isPriceOpen}
        handleCartPopupSuccess={handleCartPopupSuccess}
      />
    </>
  );
};

GoodCard.propTypes = {
  type: PropTypes.oneOf(['extraSmall', 'small', 'medium', 'large', 'horizontalSmall', 'horizontalMedium', 'review'])
    .isRequired,
  itemDecorators: PropTypes.array,
  onClick: PropTypes.func,
  goodName: PropTypes.string,
  thumbnailImageUrl01: PropTypes.string,
  thumbnailImageUrl02: PropTypes.string,
  thumbnailImageUrl03: PropTypes.string,
  originalPrice: PropTypes.number,
  originalFormattedPrice: PropTypes.number,
  discountRate: PropTypes.number,
  discountPrice: PropTypes.number,
  discountFormattedPrice: PropTypes.number,
  totalReviewScore: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  reviewCount: PropTypes.number,
  evaluationDivisionCode: PropTypes.string,
  buyCount: PropTypes.number,
  buyCountDisplay: PropTypes.bool,
  flagList: PropTypes.array,
  hideBestFlag: PropTypes.bool,
  review: PropTypes.shape({
    content: PropTypes.string,
  }),
  onReviewClick: PropTypes.func,
  goodNumber: PropTypes.string,
  adultGood: PropTypes.bool,
  wish: PropTypes.bool,
  soldOut: PropTypes.bool,
  showRelated: PropTypes.bool,
  clickLogLink: PropTypes.string,
  isEventMall: PropTypes.bool,
  isPriceOpen: PropTypes.bool,
  handleHotTopAllCategorySoldOutChangeList: PropTypes.func,
  handleViewGoodSoldOutChangeList: PropTypes.func,
  handleOrderGoodSoldOutChangeList: PropTypes.func,
  selectedCategoryNumber: PropTypes.number,
  handelBestViewGoodSoldOutChangeList: PropTypes.func,
  handleBestOrderGoodSoldOutChangeList: PropTypes.func,
  handleSubDisplayCategoryGoodSoldOutChangeList: PropTypes.func,
  handleCartPopupSuccess: PropTypes.func,
};

GoodCard.defaultProps = {
  itemDecorators: [],
  type: 'small',
};

export default GoodCard;

export const BasicGoodImage = ({ ...props }) => {
  const [cartPopupOpen, setCartPopupOpen] = useState(false);

  const {
    systemInformation: { goodCardCartBtnUseYn },
  } = useSiteInfo();

  return (
    <>
      <GoodCardImage goodCardCartBtnUseYn={goodCardCartBtnUseYn} handleCartPopupOpen={setCartPopupOpen} {...props} />
      <CartPopup open={cartPopupOpen} setCartPopupOpen={setCartPopupOpen} {...props} />
    </>
  );
};

const GoodCardRoot = styled(FlexBox)`
  height: 100%;
  align-items: flex-start;
  ${({ type }) => cardProps[type].rootCardProp};
`;

const StyledTitle = styled.div`
  width: 100%;
  font-size: ${({ size }) => size};
  line-height: ${({ lineHeight }) => lineHeight};
  color: ${(props) => props.theme.color.text.basic};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ReviewContentWrapper = styled.div`
  width: 100%;
  height: 51px;
`;

const ReviewContent = styled.div`
  width: 100%;
  font-size: 12px;
  line-height: ${moTheme.font.lineHeight.medium};
  max-height: 51px;
  height: fit-content;
  color: ${(props) => props.theme.color.text.guide};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const BlindP = styled.p`
  overflow: hidden;
  position: absolute;
  clip: rect(0, 0, 0, 0);
  clip-path: polygon(0 0, 0 0, 0 0);
  width: 1px;
  height: 1px;
  margin: -1px;
`;
